"use client";

import { useWesubCart } from "@/contexts/provider/wesub-cart-provider";
// ─── Cart Item Row ─────────────────────────────────────────────────────────────

import { TrashIcon } from "lucide-react";
import { QuantityCounter } from "@/components/common/quantity-counter";
import { useContext } from "react";
import { ResourceContext } from "@/contexts/resource-context";
import { logEvent } from "@/service/logs/log";
import {
  clubDiscountRate,
  priceBeforeClubDiscount,
} from "@/service/shop/club-discount";

export function CartItemRow({
  item,
  quantity,
  isCompany,
}: {
  item: WesubProduct;
  quantity: number;
  isCompany: boolean;
}) {
  const imageUrl = item.custom?.images?.[0] ?? item.imageUrl;
  const { name: itemName } = item;
  const { removeItem, updateQuantity, isLoading } = useWesubCart();
  const { logView } = useContext(ResourceContext);

  const itemPrice = isCompany
    ? (item.custom?.b2bMonthPrice ?? item.monthPrice ?? item.sellPrice)
    : (item.custom?.b2cMonthPrice ?? item.monthPrice ?? item.sellPrice);
  const priceLabel = isCompany ? "netto" : "brutto";

  return (
    <div className="relative flex flex-col gap-3 py-4">
      <div className="flex justify-between gap-4 pr-10">
        <div className="flex gap-4">
          <div className="size-20 min-w-20 rounded-lg bg-gray-200">
            <img
              src={imageUrl}
              alt={itemName}
              className="h-full w-full object-contain"
            />
          </div>
          <div>
            <p className="text-sm font-semibold">{itemName}</p>
            {item.custom?.status && (
              <span className="mb-1 inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                {item.custom.status}
              </span>
            )}
            <p className="text-remedium-blue mt-1 text-sm font-semibold">
              <span className="text-muted-foreground mr-1 text-xs font-normal line-through">
                {priceBeforeClubDiscount(
                  itemPrice,
                  clubDiscountRate(item),
                ).toFixed(2)}{" "}
                zł
                <br />
              </span>
              {itemPrice.toFixed(2)} zł / mies.{" "}
              <span className="text-muted-foreground text-xs">
                {priceLabel}
              </span>
            </p>
            <p className="text-muted-foreground mt-1 text-xs">
              Razem:{" "}
              <span className="font-semibold">
                {(itemPrice * quantity).toFixed(2)} zł
              </span>
            </p>
          </div>
        </div>
        <button
          className="text-muted-foreground absolute top-3 right-0 cursor-pointer p-1"
          disabled={isLoading}
          onClick={() => {
            logEvent("club_wesub_remove_from_cart", logView, {
              product_id: item.clientProductId,
              product_name: itemName,
              quantity,
            });
            removeItem(item.clientProductId);
          }}
        >
          <TrashIcon className="size-4" />
        </button>
      </div>
      <QuantityCounter
        quantity={quantity}
        setQuantity={(newQuantity) => {
          logEvent("club_wesub_update_cart_quantity", logView, {
            product_id: item.clientProductId,
            product_name: itemName,
            previous_quantity: quantity,
            new_quantity: newQuantity,
          });
          updateQuantity(item.clientProductId, newQuantity);
        }}
      />
    </div>
  );
}
